/**
 * AdminHeroTiles — Edit the 4 prominent customer-home tiles.
 * Each tile is editable in place (title, subtitle, image URL, status pill,
 * CTA target). Saves to `PATCH /api/admin/hero-tiles/{id}`.
 */
import { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ImageUploader from "../../components/ImageUploader";

const CTA_TYPES = [
    { value: "ROUTE", label: "Internal route", help: "e.g. /food, /coupons" },
    { value: "CATEGORY", label: "Business category", help: "category slug" },
    { value: "EXTERNAL", label: "External URL", help: "https://..." },
];

const STATUS_PRESETS = [
    { label: "Online", color: "#10B981" },
    { label: "Going Out", color: "#F59E0B" },
    { label: "New", color: "#3B82F6" },
    { label: "Popular", color: "#EC4899" },
    { label: "", color: "#10B981" },
];

export default function AdminHeroTiles() {
    const [tiles, setTiles] = useState([]);
    const [savingId, setSavingId] = useState(null);
    const [planBlocked, setPlanBlocked] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/hero-tiles");
            setTiles(data || []);
            setPlanBlocked(false);
        } catch (e) {
            if (e?.response?.status === 403) setPlanBlocked(true);
            else toast.error("Failed to load tiles");
        }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await load(); })();
        return () => { cancelled = true; };
    }, []);

    const update = (id, patch) => setTiles((cur) => cur.map((t) => (t.id === id ? { ...t, ...patch } : t)));

    const save = async (tile) => {
        setSavingId(tile.id);
        try {
            await api.patch(`/admin/hero-tiles/${tile.id}`, {
                title: tile.title,
                subtitle: tile.subtitle,
                image_url: tile.image_url,
                status_label: tile.status_label,
                status_color: tile.status_color,
                cta_type: tile.cta_type,
                cta_target: tile.cta_target,
                enabled: tile.enabled,
                landing_mode: tile.landing_mode || "AUTO",
            });
            toast.success(`Tile #${tile.position} saved`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSavingId(null); }
    };

    const create = async () => {
        setCreating(true);
        try {
            const { data } = await api.post("/admin/hero-tiles", {});
            setTiles((cur) => [...cur, data]);
            toast.success("Tile added");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add tile"); }
        finally { setCreating(false); }
    };

    const remove = async (tile) => {
        if (!window.confirm(`Delete tile "${tile.title}"?`)) return;
        try {
            await api.delete(`/admin/hero-tiles/${tile.id}`);
            setTiles((cur) => cur.filter((t) => t.id !== tile.id));
            toast.success("Tile deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    if (planBlocked) {
        return (
            <div data-testid="admin-hero-tiles">
                <h1 className="text-2xl font-bold">Home Tiles</h1>
                <div className="mt-6 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-2xl p-6" data-testid="hero-tiles-plan-blocked">
                    <p className="text-sm font-bold text-amber-700 dark:text-amber-300">Home Tiles editor is not included in your current plan.</p>
                    <p className="text-xs text-muted-foreground mt-1">Upgrade to <b>Growth</b> or higher (or ask the platform admin to enable it) to customize the four prominent home-page buttons.</p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-hero-tiles">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Home Tiles</h1>
                    <p className="text-sm text-muted-foreground mt-1">The prominent buttons on the customer home page. Title, image, status pill and the page they open are all editable.</p>
                </div>
                <button onClick={create} disabled={creating} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="hero-tile-add">
                    {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add Tile
                </button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {tiles.map((t) => (
                    <div key={t.id} data-testid={`hero-tile-edit-${t.position}`} className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Tile #{t.position}</span>
                            <div className="inline-flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 text-xs font-semibold">
                                    <input type="checkbox" checked={!!t.enabled} onChange={(e) => update(t.id, { enabled: e.target.checked })} className="accent-primary" /> Enabled
                                </label>
                                <button onClick={() => remove(t)} className="text-red-600 hover:text-red-700" data-testid={`hero-tile-delete-${t.position}`} title="Delete tile"><Trash2 size={14} /></button>
                            </div>
                        </div>

                        {/* Live preview */}
                        <div className="relative bg-muted/40 border border-border rounded-2xl p-4 pb-3 mb-4 min-h-[150px] overflow-hidden">
                            <div className="text-[15px] font-extrabold leading-tight uppercase tracking-wide pr-12">{t.title || "Title"}</div>
                            {t.subtitle && <div className="text-xs text-muted-foreground mt-1 pr-12">{t.subtitle}</div>}
                            {t.status_label && (
                                <span className="inline-block mt-3 text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: t.status_color || "#10B981" }}>{t.status_label}</span>
                            )}
                            {t.image_url && <img src={t.image_url} alt="" className="absolute right-2 bottom-2 w-20 h-20 object-contain" />}
                        </div>

                        <div className="space-y-2.5 text-sm">
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Title</span>
                                <input value={t.title || ""} onChange={(e) => update(t.id, { title: e.target.value })} className="block w-full bg-muted rounded-lg px-3 py-2 mt-1" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Subtitle</span>
                                <input value={t.subtitle || ""} onChange={(e) => update(t.id, { subtitle: e.target.value })} className="block w-full bg-muted rounded-lg px-3 py-2 mt-1" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Tile image</span>
                                <ImageUploader
                                    value={t.image_url}
                                    onUploaded={(url) => update(t.id, { image_url: url })}
                                    aspect="aspect-[4/3]"
                                    testId={`hero-tile-${t.position}-image`}
                                    className="mt-1"
                                />
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <label className="block">
                                    <span className="text-xs font-semibold text-muted-foreground">Status pill</span>
                                    <select value={t.status_label || ""} onChange={(e) => {
                                        const preset = STATUS_PRESETS.find((p) => p.label === e.target.value);
                                        update(t.id, { status_label: e.target.value || null, status_color: preset?.color || t.status_color });
                                    }} className="block w-full bg-muted rounded-lg px-3 py-2 mt-1">
                                        <option value="">— none —</option>
                                        {STATUS_PRESETS.filter((p) => p.label).map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-muted-foreground">Pill color</span>
                                    <input type="color" value={t.status_color || "#10B981"} onChange={(e) => update(t.id, { status_color: e.target.value })} className="block w-full bg-muted rounded-lg px-1 py-1 mt-1 h-[38px]" />
                                </label>
                            </div>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Opens (CTA type)</span>
                                <select value={t.cta_type || "ROUTE"} onChange={(e) => update(t.id, { cta_type: e.target.value })} className="block w-full bg-muted rounded-lg px-3 py-2 mt-1">
                                    {CTA_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Target</span>
                                <input value={t.cta_target || ""} onChange={(e) => update(t.id, { cta_target: e.target.value })} placeholder={CTA_TYPES.find((c) => c.value === t.cta_type)?.help} className="block w-full bg-muted rounded-lg px-3 py-2 mt-1" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Browse layout on tap</span>
                                <select
                                    data-testid={`hero-tile-landing-${t.position}`}
                                    value={t.landing_mode || "AUTO"}
                                    onChange={(e) => update(t.id, { landing_mode: e.target.value })}
                                    className="block w-full bg-muted rounded-lg px-3 py-2 mt-1"
                                >
                                    <option value="AUTO">Auto (tenant default / theme preset)</option>
                                    <option value="SHOPS">Browse by Shops (Swiggy-style)</option>
                                    <option value="PRODUCTS">Browse by Products (Flipkart-style)</option>
                                </select>
                                <p className="text-[11px] text-muted-foreground mt-1">Overrides the tenant-wide default for this tile. AUTO falls back to your Branding setting → theme preset.</p>
                            </label>
                        </div>

                        <button data-testid={`hero-tile-save-${t.position}`} disabled={savingId === t.id} onClick={() => save(t)} className="mt-4 bg-primary text-primary-foreground rounded-xl px-4 py-2 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                            {savingId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
